import { useMemo, useState } from 'react';
import type { BrouillonItem, ChangementItem, Item, MembrePublic } from '@camellie/shared';
import { jourDeEcheance, TYPES_ROUTINE_ENFANT } from '@camellie/shared';
import { LigneItem } from '../composants/LigneItem';
import { BasculeVue } from '../composants/BasculeVue';
import { FicheItem } from '../composants/FicheItem';
import { aujourdhui, FUSEAU_FAMILLE, majuscule } from '../lib/format';

interface Props {
  items: Item[];
  membres: MembrePublic[];
  moi: MembrePublic;
  vueTout: boolean;
  onVueTout: (v: boolean) => void;
  onBasculer: (id: string) => void;
  onAjouter: (brouillon: BrouillonItem) => void;
  onModifier: (id: string, champs: ChangementItem) => void;
  onSupprimer: (id: string) => void;
}

const JOURS = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];

const fMois = new Intl.DateTimeFormat('fr-CA', {
  timeZone: 'UTC',
  month: 'long',
  year: 'numeric',
});

const fJourLong = new Intl.DateTimeFormat('fr-CA', {
  timeZone: 'UTC',
  weekday: 'long',
  day: 'numeric',
  month: 'long',
});

/**
 * Midi UTC, et formatage en UTC : la case « 14 » doit afficher 14 quel que
 * soit le fuseau du telephone. Ces dates-la sont des etiquettes de calendrier,
 * pas des instants — contrairement aux echeances, qui passent bien par le
 * fuseau de la famille.
 */
function dateDe(jour: string): Date {
  return new Date(jour + 'T12:00:00Z');
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function jourDe(d: Date): string {
  return d.getUTCFullYear() + '-' + pad(d.getUTCMonth() + 1) + '-' + pad(d.getUTCDate());
}

/**
 * Les cases du mois, de dimanche a samedi, completees avant et apres pour
 * former des semaines entieres. Les jours hors du mois sont rendus mais
 * grises : une grille a trous se lit plus mal qu'une grille pleine.
 */
function grilleDuMois(mois: string): { jour: string; dansLeMois: boolean }[] {
  const [a, m] = mois.split('-').map(Number);
  const premier = new Date(Date.UTC(a!, m! - 1, 1, 12));
  const dernier = new Date(Date.UTC(a!, m!, 0, 12));

  const curseur = new Date(premier);
  curseur.setUTCDate(curseur.getUTCDate() - premier.getUTCDay());
  const fin = new Date(dernier);
  fin.setUTCDate(fin.getUTCDate() + (6 - dernier.getUTCDay()));

  const cases: { jour: string; dansLeMois: boolean }[] = [];
  while (curseur <= fin) {
    const jour = jourDe(curseur);
    cases.push({ jour, dansLeMois: jour.slice(0, 7) === mois });
    curseur.setUTCDate(curseur.getUTCDate() + 1);
  }
  return cases;
}

function moisVoisin(mois: string, delta: number): string {
  const [a, m] = mois.split('-').map(Number);
  const d = new Date(Date.UTC(a!, m! - 1 + delta, 1, 12));
  return d.getUTCFullYear() + '-' + pad(d.getUTCMonth() + 1);
}

/**
 * L'agenda : une grille mensuelle pour la vue d'ensemble, le detail du jour
 * choisi en dessous.
 *
 * Pourquoi pas le titre des elements directement dans les cases, comme sur un
 * calendrier mural : sept colonnes sur 375 px font 50 px chacune, aucun titre
 * n'y tient. La grille porte donc des pastilles — une par element, a la
 * couleur de la personne concernee — qui repondent a « quel jour est charge ».
 * Le detail lisible s'affiche des qu'on touche un jour.
 *
 * Les elements deja faits apparaissent dans ce detail : c'est le seul endroit,
 * avec l'accueil, ou l'on peut decocher quelque chose coche par erreur la
 * semaine derniere.
 */
export function EcranAgenda({
  items,
  membres,
  moi,
  vueTout,
  onVueTout,
  onBasculer,
  onAjouter,
  onModifier,
  onSupprimer,
}: Props) {
  const aujourd = aujourdhui();
  const [mois, setMois] = useState(aujourd.slice(0, 7));
  const [jourChoisi, setJourChoisi] = useState(aujourd);
  const [enEdition, setEnEdition] = useState<string | null>(null);
  const [creation, setCreation] = useState(false);

  const parId = useMemo(() => new Map(membres.map((m) => [m.id, m])), [membres]);

  const { parJour, caches } = useMemo(() => {
    const mien = (i: Item) => i.assigne_a === moi.id || i.assigne_a === null;
    // Habillage et lunch reviennent chaque jour : dans l'agenda familial ils
    // noieraient les vrais rendez-vous. Ils ont leur propre ecran.
    const dates = items.filter(
      (i) => i.du_le && i.statut !== 'annule' && !TYPES_ROUTINE_ENFANT.includes(i.type),
    );
    const visibles = vueTout ? dates : dates.filter(mien);

    const carte = new Map<string, Item[]>();
    for (const it of visibles) {
      const jour = jourDeEcheance(it.du_le!, FUSEAU_FAMILLE);
      if (!jour) continue;
      const liste = carte.get(jour);
      if (liste) liste.push(it);
      else carte.set(jour, [it]);
    }
    for (const liste of carte.values()) {
      liste.sort((a, b) => (a.du_le ?? '').localeCompare(b.du_le ?? ''));
    }
    return { parJour: carte, caches: dates.filter((i) => !mien(i)).length };
  }, [items, moi.id, vueTout]);

  const cases = useMemo(() => grilleDuMois(mois), [mois]);
  const duJour = parJour.get(jourChoisi) ?? [];
  const item = enEdition ? (items.find((i) => i.id === enEdition) ?? null) : null;

  function allerA(nouveauMois: string) {
    setMois(nouveauMois);
    // Revenir au mois courant reselectionne aujourd'hui ; sinon on se pose
    // sur le 1er, faute de meilleur candidat.
    setJourChoisi(nouveauMois === aujourd.slice(0, 7) ? aujourd : nouveauMois + '-01');
  }

  return (
    <>
      <div className="entete">
        <h1>{majuscule(fMois.format(dateDe(mois + '-01')))}</h1>
        <div className="entete-actions">
          <button
            type="button"
            className="fleche"
            onClick={() => allerA(moisVoisin(mois, -1))}
            aria-label="Mois precedent"
          >
            ‹
          </button>
          <button
            type="button"
            className="fleche"
            onClick={() => allerA(moisVoisin(mois, 1))}
            aria-label="Mois suivant"
          >
            ›
          </button>
        </div>
      </div>

      <div className="contenu">
        <BasculeVue vueTout={vueTout} onChange={onVueTout} caches={caches} />

        <div className="mois">
          {JOURS.map((j, i) => (
            <div key={i} className="mois-entete" aria-hidden="true">
              {j}
            </div>
          ))}

          {cases.map(({ jour, dansLeMois }) => {
            const restant = (parJour.get(jour) ?? []).filter((i) => i.statut === 'todo');
            const classes = [
              'case-jour',
              dansLeMois ? '' : 'hors-mois',
              jour === aujourd ? 'aujourdhui' : '',
              jour === jourChoisi ? 'choisi' : '',
            ]
              .filter(Boolean)
              .join(' ');

            return (
              <button
                key={jour}
                type="button"
                className={classes}
                onClick={() => setJourChoisi(jour)}
                aria-pressed={jour === jourChoisi}
                aria-label={fJourLong.format(dateDe(jour)) + ', ' + String(restant.length) + ' element(s)'}
              >
                <span className="numero">{Number(jour.slice(8))}</span>
                <span className="points" aria-hidden="true">
                  {/* Au-dela de trois, la case deborderait : on en montre
                      trois, le detail du jour dira le reste. */}
                  {restant.slice(0, 3).map((i) => (
                    <span
                      key={i.id}
                      className="point"
                      style={{
                        background: i.assigne_a
                          ? (parId.get(i.assigne_a)?.couleur ?? 'var(--texte-faible)')
                          : 'var(--texte-faible)',
                      }}
                    />
                  ))}
                </span>
              </button>
            );
          })}
        </div>

        {mois !== aujourd.slice(0, 7) && (
          <button
            type="button"
            className="bouton discret pleine-largeur"
            onClick={() => allerA(aujourd.slice(0, 7))}
          >
            Revenir a aujourd hui
          </button>
        )}

        <section className="section">
          <h2>{majuscule(fJourLong.format(dateDe(jourChoisi)))}</h2>

          {duJour.length === 0 ? (
            <div className="vide">Rien ce jour-la.</div>
          ) : (
            <div className="liste">
              {duJour.map((i) => (
                <LigneItem
                  key={i.id}
                  item={i}
                  membres={parId}
                  moi={moi.id}
                  onBasculer={onBasculer}
                  onOuvrir={setEnEdition}
                />
              ))}
            </div>
          )}

          <button
            type="button"
            className="ajouter-gros"
            style={{ marginTop: 'var(--r3)', marginBottom: 0 }}
            onClick={() => setCreation(true)}
          >
            <span aria-hidden="true">+</span> Ajouter un rendez-vous
          </button>
        </section>
      </div>

      {(item || creation) && (
        <FicheItem
          item={item}
          typeNouveau="rdv"
          dateParDefaut={jourChoisi}
          membres={membres}
          moi={moi.id}
          onEnregistrer={onModifier}
          onCreer={onAjouter}
          onSupprimer={onSupprimer}
          onFermer={() => {
            setEnEdition(null);
            setCreation(false);
          }}
        />
      )}
    </>
  );
}
